import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'
import Image from '~/components/image/Image'
import { useIsPC } from '~/lib/ua'
import { css, sva } from '~/styled-system/css'
import type { RecipeVariantProps } from '~/styled-system/css'
import { OverlayScrollButton } from '~/ui/IconButton'
import type { Banner } from './types'

type TopBannerProps = {
  banners: Banner[]
}

export const TopBanner: FC<TopBannerProps> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgTopBanner : IkyuTopBanner

function PpgTopBanner({ banners }: TopBannerProps) {
  const isPc = useIsPC()
  const TopBannerContent = isPc ? PcTopBannerContent : SdTopBannerContent
  return <TopBannerContent banners={banners} mode="ppg" />
}

function IkyuTopBanner({ banners }: TopBannerProps) {
  const isPc = useIsPC()
  const TopBannerContent = isPc ? PcTopBannerContent : SdTopBannerContent
  return <TopBannerContent banners={banners} />
}

const styles = sva({
  slots: ['container', 'slideLink', 'overlay', 'content', 'title', 'description'],
  base: {
    container: {
      pos: 'relative',
      w: 'full',
      isolation: 'isolate',
    },
    slideLink: {
      display: 'block',
    },
    overlay: {
      display: 'flex',
      pos: 'absolute',
      inset: '0',
      justifyContent: 'center',
      alignItems: 'center',
      bg: 'black/40',
    },
    content: {
      w: 'full',
      spaceY: '2',
      textAlign: 'center',
      color: 'white',
    },
    title: {
      fontWeight: 'bold',
    },
    description: {
      fontSize: { base: 'base', _pc: 'lg' },
    },
  },
  variants: {
    mode: {
      ikyu: {
        content: {
          transform: 'translateY(-25%)',
          px: '4',
        },
        title: {
          fontSize: { base: '3xl', _pc: '4xl' },
        },
      },
      ppg: {
        overlay: {
          bg: 'black/20',
        },
        content: {
          transform: 'translateY(0)',
          px: '2',
        },
        title: {
          fontSize: { base: '4xl', _pc: '[28px]' },
        },
      },
    },
  },
  defaultVariants: {
    mode: 'ikyu',
  },
})

type TopBannerContentProps = {
  banners: Banner[]
} & RecipeVariantProps<typeof styles>

function SdTopBannerContent({ banners, mode }: TopBannerContentProps) {
  const classes = styles({ mode })
  return (
    <div className={classes.container}>
      <Swiper
        modules={[Pagination, Autoplay]}
        pagination={{
          el: '.swiper-pagination',
        }}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop={true}
      >
        {banners.map((banner) => {
          return (
            <SwiperSlide key={banner.object_id}>
              <a href={banner.link} className={classes.slideLink}>
                {/* TODO: JSON_LD に統一する */}
                {/* https://github.com/ikyucom/macaron-frontend/issues/4090 */}
                <meta itemProp="image" content={banner.image.url} />
                <Image
                  src={banner.image.url}
                  alt={banner.image.alt}
                  width={700}
                  height={350}
                  usePlaceholder={true}
                />
                <div className={classes.overlay}>
                  <div className={classes.content}>
                    <p className={classes.title}>{banner.title}</p>
                    <p className={classes.description}>{banner.description}</p>
                  </div>
                </div>
              </a>
            </SwiperSlide>
          )
        })}
      </Swiper>
      <div className="swiper-pagination" style={{ bottom: '15%' }} />
    </div>
  )
}

function PcTopBannerContent({ banners: _banners, mode }: TopBannerContentProps) {
  const classes = styles({ mode })

  // 初回レンダリング時は Swiper を使用しない
  const [initialized, setInitialized] = useState(false)
  useEffect(() => {
    setInitialized(true)
  }, [])

  // Navigation module (https://swiperjs.com/swiper-api#navigation)
  // が効かないので明示的に操作
  const swiper = useRef<SwiperClass | null>(null)
  const next = useCallback(() => swiper.current?.slideNext(), [])
  const prev = useCallback(() => swiper.current?.slidePrev(), [])

  // 3枚以下の場合、大きな余白ができてしまいが表示されてしまうので、2倍にしてループさせる
  // @see https://github.com/ikyucom/macaron-frontend/issues/4743
  const banners = [..._banners, ..._banners]

  return (
    <div className={classes.container}>
      {initialized ? (
        <>
          <Swiper
            modules={[Autoplay]}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            slidesPerView="auto"
            centeredSlides
            updateOnWindowResize
            loop
            onSwiper={(s) => (swiper.current = s)}
            role="group"
          >
            {banners.map((banner, i) => {
              return (
                <SwiperSlide
                  key={`${banner.object_id}-${i}`}
                  className={css({ display: 'flex!', w: '[1000!px]', h: '[250!px]' })}
                >
                  <a href={banner.link} className={classes.slideLink}>
                    {/* TODO: JSON_LD に統一する */}
                    {/* https://github.com/ikyucom/macaron-frontend/issues/4090 */}
                    <meta itemProp="image" content={banner.image.url} />
                    <Image
                      src={banner.image.url}
                      alt={banner.image.alt}
                      width={1000}
                      height={250}
                      usePlaceholder={true}
                      className={css({ w: '[100%]' })}
                    />
                    <div className={classes.overlay}>
                      <div className={classes.content}>
                        <p className={classes.title}>{banner.title}</p>
                        <p className={classes.description}>{banner.description}</p>
                      </div>
                    </div>
                  </a>
                </SwiperSlide>
              )
            })}
            <OverlayScrollButton onClick={prev} direction="left" />
            <OverlayScrollButton onClick={next} direction="right" />
          </Swiper>
        </>
      ) : (
        <div className={css({ display: 'flex', overflowX: 'hidden' })}>
          {_banners.map((banner, i) => {
            return (
              <div
                key={`${banner.object_id}-${i}`}
                className={css({ display: 'block', flexShrink: '0', w: '[1000px]', h: '[250px]' })}
              >
                <a href={banner.link} className={classes.slideLink}>
                  <meta itemProp="image" content={banner.image.url} />
                  <Image
                    src={banner.image.url}
                    alt={banner.image.alt}
                    width={1000}
                    height={250}
                    usePlaceholder={true}
                  />
                </a>
                <div className={classes.overlay}></div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
